

import Vue from 'vue';
import {BoPanel} from "bonomo";
import Component from "vue-class-component";
import {
  IContract
} from "@/model/Contract/contractInterfaces";
import {namespace} from "vuex-class";
import {Prop, Watch} from "vue-property-decorator";
import {IOnOffHandlers} from "bonomo";
import {IShortLocker, IShortUser} from "@/model/shortInterfaces";
import {IErrors} from "@/model/IErrors";
import LockerChip from "@/components/shared/chips/LockerChip.vue";
import BoDateField from "@/components/shared/BoDateField.vue";
import {Route} from "vue-router";
import {LocaleMessages} from "vue-i18n";
import BoUserField from "@/components/user/widget/BoUserField.vue";
import ContractRenewDialog from "@/components/settings/contracts/ContractRenewDialog.vue";

const lockerModule = namespace("lockerModule");
const lockerContractModule = namespace("lockerContractModule");
const userModule = namespace("userModule");

// @todo @anyone abstract this
enum EFormModes {
  INIT = 0,
  CREATE = 1,
  VIEW = 2,
  EDIT = 3,
}

const cleanData: IContract = {
  additional_comments: '',
  beginning_contract_date: '',
  client_name : '',
  contact_person: '',
  cost_center: 'Madrid',
  discount: 0,
  end_contract_date: '',
  frequency_invoicing: 'monthly',
  installation_cost: null,
  installation_date: '',
  invoice_company: 'lavalocker',
  invoicing_guarantee_date: '',
  invoicing_install_date: '',
  invoicing_maintenance_date: '',
  invoicing_rental_date: '',
  invoicing_sales_date: '',
  IPC_increase: null,
  IPC_increase_date: '',
  key_account_manager_id: null,
  locker_condition: 'active',
  locker_id: 0,
  monthly_amount_invoice: null,
  payment_terms: null,
  production_cost: null,
  renewal_conditions: 'automatic',
  stock_company: 'lavalocker',
  transaction_type: 'rent',

  discount_reason: null,
  location: 'national',
  invoicing_additional_column: null,
  payment_type: 'direct_debit',
  renovation_notice_date: '',
  expansion_renovation: null,
  last_renovation_date: '',
  purchase_order: '',
  setup_and_training: null,
  rear: '',
  extra_costs: 0,
};

@Component({
  name: 'ContractForm',
  components: {
    LockerChip,
    BoPanel,
    BoUserField,
    BoDateField,
    ContractRenewDialog,
  }
})
export default class ContractForm extends Vue {

  @Prop({ default: 0 })
  private lockerId!: number;

  // @Prop({ default: 0 }) // contractId === 0 means we are in creation, we are in edit otherwise
  // private contractId!: number;
  @Prop({ default: 0 }) // contractId === 0 means we are in creation, we are in edit otherwise
  private entityId!: number;

  @lockerContractModule.Getter('contract')
  // that's why we should call internal data just data (eg not contract)
  private contractInStore!: IContract;
  @lockerContractModule.Getter('errors')
  protected errors!: IErrors;
  @lockerContractModule.Action('createContract')
  private createContract!: (data: IContract) => void;
  @lockerContractModule.Action('fetchContract')
  private fetchContract!: (params: {lockerId: number, contractId: number}) => void;
  @lockerContractModule.Action('updateContract')
  private updateContract!: (contract: IContract) => void;
  // @lockerContractModule.Action('clearError')
  // private clearError!: (ref: string) => void;

  @lockerModule.Getter('locker')
  private locker!: IShortLocker;
  @lockerModule.Action('fetchLocker')
  private fetchLocker!: (lockerId: any) => void;

  protected EFormModes: any = EFormModes;

  protected loading: boolean = false;
  protected submitting: boolean = false;
  // now it is ambiguous
  protected mode: EFormModes = EFormModes.INIT;
  private subscribes: IOnOffHandlers = {};
  protected contract: IContract = Object.assign({}, cleanData);
  private showRenewDialog = false;

  // editing is disabled when only viewing, and while loading and submitting
  protected get editingDisabled(): boolean {
    return (this.mode == EFormModes.VIEW) || this.loading || this.submitting;
  }

  protected get editButtonDisabled(): boolean {
    // this is now strange ambiguous logic as is meant for a quick fix so edit button doesn't show when trying to load an invalid contract ID
    return this.loading ||
        (+this.lockerId != +this.contract.locker_id) ||
        (!this.contract.id || (+this.entityId != +this.contract.id));
  }

  protected get panelTitle(): string {
    let title = '...';
    // we can return a nice title with shipment ID once we implement editing
    switch (this.mode) {
      case this.EFormModes.CREATE:
        title = 'contracts.contract.title.createContract';
        break;
      case this.EFormModes.VIEW:
        title = 'contracts.contract.title.viewContract';
        break;
      case this.EFormModes.EDIT:
        title = 'contracts.contract.title.editContract';
        break;
    }
    return this.$t(title, {entityId: this.entityId}) as string;
  }

  // @todo @anyone ambiguous URLs should be denied eg. if lockerId = 0
  @Watch('$route')
  private onRouteChanged(newRoute: Route) {
    // if (newRoute.params.entityId) {
    if (newRoute.params.lockerId && newRoute.params.entityId) {
      this.mode = EFormModes.VIEW;
      this.loadData();
    }
    else if (newRoute.params.lockerId) {
      this.mode = EFormModes.CREATE;
      this.clearData();
    }
    else {
      this.$router.push({name: 'home'});
    }
    if (!this.locker || this.locker.id != this.lockerId) {
      // @todo @anyone I should clean the current locker first so it wouldn't display the old data while loading the new, when changing locker
      // this could so should be done generally within fetchLocker, eg it should clean locker data first - OH, and this should be done with all fetchXxx in general, at least from now on
      this.fetchLocker(this.lockerId);
    }
  }

  // avoid visibility for created() and beforeDestroy as no reference found (private for sure not, probably should be
  //  public but let's respect the 5000000000000 flies theory)
  created() {
    this.subscribes = this.$ons({
      'backend.call.OK.contractLoaded': this.onContractLoadedOK,
      'backend.call.KO.contractLoaded': this.onContractLoadedKO,
    });
    this.onRouteChanged(this.$route);
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  protected isMode(mode: EFormModes): boolean {
    return this.mode === mode;
  }

  // @todo @sometime: this should work just as return this.$selectItemsMaybeBetterGlobalName('contracts.contract.fields', label);
  protected selectItems(label: string): any[] {
    const items: LocaleMessages = this.$t('contracts.contract.fields.' + label + '.values') as LocaleMessages;
    return Object.entries(items).map((eachItem) => {
      return {text: eachItem[1], value: eachItem[0]};
    });
  }

  protected onKamUserSelected(user: IShortUser) {
    if (this.contract.keyAccountManager) {
      Object.assign(this.contract.keyAccountManager, user);
    }
    else {
      Vue.set(this.contract, 'keyAccountManager', user);
    }
    this.contract.key_account_manager_id = user.id;
  }

  protected loadData() {
    this.loading = true;
    this.clearData();
    this.fetchContract({lockerId: +this.lockerId, contractId: +this.entityId});
  }

  protected onEdit(): void {
    this.mode = EFormModes.EDIT;
  }

  protected onCancelEdit(): void {
    Object.assign(this.contract, this.contractInStore);
    this.mode = EFormModes.VIEW;
  }
  protected onSave() {
    // if (!this.formIsValid) {
    //   return;
    // }
    // I should return here if data is not dirty but this doesn't seem to work
    // console.log('dirty? ' + _.isEqual(this.data, this.shipment) ? '=' : '!=');
    this.submitting = true;
    this.updateContract(Object.assign({}, this.contract));
  }
  protected onSaveNew() {
    // if (!this.formIsValid) {
    //   return;
    // }
    this.submitting = true;
    this.createContract(Object.assign({}, this.contract));
  }

  private onContractLoadedOK() {
    switch (this.mode) {
      case EFormModes.CREATE:
        this.$router.push({name: 'lockersContracts', params: {lockerId: ''+this.lockerId}})
        break;
      case EFormModes.EDIT:
        this.mode = EFormModes.VIEW;
        // eslint-disable-next-line no-fallthrough because racecar
      case EFormModes.VIEW:
        // this will redirect to the correct URL if a contract from another locker is loaded. Wrapped in nextTick() as data might not be available yet when we get here (though should)
        this.$nextTick(() => {
          if (+this.lockerId != +this.contract.locker_id) {
            this.$router.push({name: 'lockersContracts', params: {lockerId: ''+this.contract.locker_id, entityId: ''+this.entityId}});
          }
        })
        Object.assign(this.contract, this.contractInStore);
        this.loading = false;
        this.submitting = false;
        break;
    }
  }
  private onContractLoadedKO() {
      this.clearLoadingAndLikes();
  }

  private clearLoadingAndLikes(): void {
    this.loading = false;
    this.submitting = false;
  }

  private clearData(): void {
    // maybe I should clear errors here too? :D - no problem as we don't validate on frontend and next submit will overwrite errors (hopefully!)
    Object.assign(this.contract, cleanData);
    this.contract.locker_id = this.lockerId;
  }

  private onOpenRenewDialog() {
    this.showRenewDialog = true;
  }

  private onCloseRenewDialog() {
    this.showRenewDialog = false;
  }

}

